import React, { Component } from "react";
import PropTypes from "prop-types";
import {
    DialogBox,
    DialogBoxFooterType,
    NoLink,
    Section,
    SelectInput,
    SubSection,
    ZeroToInfiniteGrid,
} from "~/core";
import { withEditableGrid } from "~/hocs";
import {
    mapToPicklistValue,
    prepareSelectableOptions,
    hasOnlyInActiveOptions,
} from "~/admin/utils";
import { messages } from "~/admin/agBytes/i18n-messages";
import { createAddLinkLabelText, createAddEditModalTitle } from "~/i18n-messages";
import { adminData, GUID, ID, NAME, PICKLIST_GUID, VALUE } from "~/admin/data";
const PROPS_CROP_CLASSNAME_GUID = "cropClassNameGuid";
const PROPS_CROP_CLASS_NAME = "cropClassName";
const PROPS_CROP_NAME = "cropName";
const PROPS_CROP_PURPOSE = "cropPurpose";
const PROPS_CROP_PURPOSE_GUID = "cropPurposeGuid";
const PROPS_CROP_PURPOSE_NAME = "cropPurposeName";
const PROP_CROP_GUID = "cropGuid";

class CropList extends Component {
    static propTypes = {
        record: PropTypes.array,
        formatMessage: PropTypes.func,
        toggleCropValidState: PropTypes.func,
        toggleModal: PropTypes.func,
        onChange: PropTypes.func,
        renderDeleteModal: PropTypes.func,
        getCropClassNames: PropTypes.func,
        options: PropTypes.object,
        onAdd: PropTypes.func,
        onEdit: PropTypes.func,
        onDelete: PropTypes.func,
        onAddOrEditItem: PropTypes.func,
        deleteItem: PropTypes.func,
        isEditing: PropTypes.bool,
        isDeleting: PropTypes.bool,
        editData: PropTypes.object,
    };

    constructor(props) {
        super(props);
        this.state = {
            cropName: [],
            cropPurpose: [],
            cropClassName: [],
            editingItem: false,
            records: [],
            lastRecord: null,
            lastCropNameOptions: null,
            lastCropPurposeOptions: null,
            lastCropClassNameOptions: null,
        };
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        const { record, options = {} } = nextProps;
        const cropNameOptions = options[PROPS_CROP_NAME];
        const cropPurposeOptions = options[PROPS_CROP_PURPOSE];
        const cropClassNameOptions = options[PROPS_CROP_CLASS_NAME];
        if (
            record === prevState.lastRecord &&
            cropNameOptions === prevState.lastCropNameOptions &&
            cropPurposeOptions === prevState.lastCropPurposeOptions &&
            cropClassNameOptions === prevState.lastCropClassNameOptions
        ) {
            return null;
        }
        const records = (record || []).map((recordItem) => {
            const selectedCropName = (cropNameOptions || []).find(
                (cropNameItem) => cropNameItem[GUID] === recordItem[PROP_CROP_GUID]
            );
            const selectedCropPurpose = (cropPurposeOptions || []).find(
                (cropPurposeItem) =>
                    cropPurposeItem[PICKLIST_GUID] === recordItem[PROPS_CROP_PURPOSE_GUID]
            );
            const selectedCropClassName = (cropClassNameOptions || []).find(
                (cropClassNameItem) =>
                    cropClassNameItem[GUID] === recordItem[PROPS_CROP_CLASSNAME_GUID]
            );
            return {
                ...recordItem,
                [PROPS_CROP_NAME]:
                    (selectedCropName && selectedCropName[NAME]) || recordItem[PROPS_CROP_NAME],
                [PROPS_CROP_PURPOSE_NAME]:
                    (selectedCropPurpose && selectedCropPurpose[VALUE]) ||
                    recordItem[PROPS_CROP_PURPOSE_NAME],
                [PROPS_CROP_CLASS_NAME]:
                    (selectedCropClassName && selectedCropClassName[NAME]) ||
                    recordItem[PROPS_CROP_CLASS_NAME],
            };
        });
        const nextState = {
            records,
            lastRecord: record,
            lastCropNameOptions: cropNameOptions,
            lastCropPurposeOptions: cropPurposeOptions,
            lastCropClassNameOptions: cropClassNameOptions,
        };
        if (cropClassNameOptions !== prevState.lastCropClassNameOptions) {
            nextState.cropClassName = prepareSelectableOptions(cropClassNameOptions, {
                guid: GUID,
                label: NAME,
                id: ID,
            });
        }
        return nextState;
    }

    componentDidUpdate(prevProps) {
        if (!this.props.isDeleting && prevProps.isDeleting && this.props.toggleCropValidState) {
            this.props.toggleCropValidState(this.props.record.length > 0);
        }
    }

    initializeDropdowns = (options, isAdd = false) => {
        this.initializeCropName(options);
        this.initializeCropPurpose(options);
        if (!isAdd) {
            this.initializeCropClassName(options);
        }
    };

    initializeCropClassName = (options) => {
        this.setState({
            cropClassName: prepareSelectableOptions(options[PROPS_CROP_CLASS_NAME], {
                guid: GUID,
                label: NAME,
                id: ID,
            }),
        });
    };

    initializeCropName = (options) => {
        this.setState({
            cropName: prepareSelectableOptions(options[PROPS_CROP_NAME], {
                guid: GUID,
                label: NAME,
                id: ID,
            }),
        });
    };

    initializeCropPurpose = (options) => {
        this.setState({
            cropPurpose: prepareSelectableOptions(options[PROPS_CROP_PURPOSE], {
                guid: PICKLIST_GUID,
                label: VALUE,
                id: ID,
            }),
        });
    };

    onEdit = (record, index) => {
        if (record[PROP_CROP_GUID] && this.props.getCropClassNames) {
            const cropGuid = record[PROP_CROP_GUID];
            this.props.getCropClassNames({ cropGuid });
        }
        this.initializeDropdowns(this.props.options);
        this.setState({ editingItem: true });
        const keysToUpdate = [
            {
                name: [PROP_CROP_GUID],
                key: GUID,
            },
            {
                name: [PROPS_CROP_NAME],
                key: NAME,
            },
            {
                name: [PROPS_CROP_PURPOSE_GUID],
                key: PICKLIST_GUID,
            },
            {
                name: [PROPS_CROP_PURPOSE_NAME],
                key: VALUE,
            },
            {
                name: [PROPS_CROP_CLASSNAME_GUID],
                key: GUID,
            },
            {
                name: [PROPS_CROP_CLASS_NAME],
                key: [NAME],
            },
        ];
        this.props.onEdit({ record, index, keysToUpdate });
    };

    onDelete = (record, index) => {
        this.props.onDelete({ record, index });
    };

    onCropNameChange = (guid, name) => {
        this.props.onChange({
            [PROP_CROP_GUID]: guid,
            [PROPS_CROP_NAME]: name,
            [PROPS_CROP_CLASS_NAME]: "",
            [PROPS_CROP_CLASSNAME_GUID]: "",
        });
        if (this.props.getCropClassNames) {
            const cropGuid = guid;
            this.props.getCropClassNames({ cropGuid });
        }
    };

    onAdd = () => {
        this.initializeDropdowns(this.props.options, true);
        this.setState({ editingItem: false });
        this.props.onAdd();
    };

    onAction = () => {
        this.props.onAddOrEditItem();
        if (this.props.toggleCropValidState) {
            this.props.toggleCropValidState(true);
        }
    };

    renderAddEditModal = () => {
        const { formatMessage, isEditing, editData, toggleModal } = this.props;
        return (
            <DialogBox
                actionDisabled={!editData[PROP_CROP_GUID]}
                footerType={DialogBoxFooterType.ACTION_CANCEL}
                forceOverflow
                isOpen={isEditing}
                onAction={() => this.onAction()}
                onClose={() => toggleModal("isEditing", false)}
                testId="equation-crop-list-add-edit-modal"
                title={createAddEditModalTitle(
                    this.state.editingItem,
                    formatMessage,
                    messages.crop
                )}
            >
                <div className="dialog-box-select">
                    <Section testId="equation-crop-list-crop-section">
                        <SubSection testId="equation-crop-list-crop-purpose-subsection">
                            <SelectInput
                                required
                                tabIndex={0}
                                options={this.state.cropName}
                                optionIsHiddenKey={adminData.PROPS_ACTIVE_YN}
                                autoFocus
                                openOnFocus={false}
                                placeholderText={formatMessage(messages.crop)}
                                value={mapToPicklistValue({
                                    options: this.state.cropName,
                                    selectedGuid: editData[PROP_CROP_GUID],
                                })}
                                onChange={(value) => {
                                    const guid = value ? value.guid : "";
                                    const name = value ? value.name : "";
                                    this.props.onChange({
                                        [PROP_CROP_GUID]: guid,
                                    });
                                    this.onCropNameChange(guid, name);
                                }}
                                testId="equation-crop-list-crop-select"
                            />
                            <SelectInput
                                tabIndex={0}
                                optionIsHiddenKey={adminData.PROPS_ACTIVE_YN}
                                options={this.state.cropPurpose}
                                placeholderText={formatMessage(messages.cropPurpose)}
                                value={mapToPicklistValue({
                                    options: this.state.cropPurpose,
                                    selectedGuid: editData[PROPS_CROP_PURPOSE_GUID],
                                })}
                                onChange={(value) => {
                                    this.props.onChange({
                                        [PROPS_CROP_PURPOSE_GUID]: value ? value.guid : "",
                                        [PROPS_CROP_PURPOSE_NAME]: value ? value.name : "",
                                    });
                                }}
                                testId="equation-crop-list-crop-purpose-select"
                            />
                        </SubSection>
                        <SubSection testId="equation-crop-list-crop-class-name-subsection">
                            {!this.state.cropClassName.length > 0 ||
                            hasOnlyInActiveOptions(this.state.cropClassName) ? null : (
                                <SelectInput
                                    tabIndex={0}
                                    optionIsHiddenKey={adminData.PROPS_ACTIVE_YN}
                                    options={this.state.cropClassName}
                                    placeholderText={formatMessage(messages.cropClassName)}
                                    value={mapToPicklistValue({
                                        options: this.state.cropClassName,
                                        selectedGuid: editData[PROPS_CROP_CLASSNAME_GUID],
                                    })}
                                    onChange={(value) => {
                                        this.props.onChange({
                                            [PROPS_CROP_CLASSNAME_GUID]: value ? value.guid : "",
                                            [PROPS_CROP_CLASS_NAME]: value ? value.name : "",
                                        });
                                    }}
                                    testId="equation-crop-list-crop-class-name-select"
                                />
                            )}
                        </SubSection>
                    </Section>
                </div>
            </DialogBox>
        );
    };

    render() {
        const { formatMessage } = this.props;
        const { records } = this.state;
        return (
            <div className={"form-section-child-stretch mini-grid"}>
                {!(records && records.length > 0) ? null : (
                    <ZeroToInfiniteGrid
                        records={records}
                        columns={{
                            [PROPS_CROP_NAME]: {
                                title: formatMessage(messages.crop),
                            },
                            [PROPS_CROP_PURPOSE_NAME]: {
                                title: formatMessage(messages.cropPurpose),
                            },
                        }}
                        onEdit={this.onEdit}
                        onDelete={this.onDelete}
                    />
                )}
                {this.renderAddEditModal()}
                {this.props.renderDeleteModal()}
                <div className="add-link-container">
                    <NoLink
                        className="add-link"
                        label={createAddLinkLabelText(formatMessage, messages.crop)}
                        onClick={this.onAdd}
                    ></NoLink>
                </div>
            </div>
        );
    }
}

export default withEditableGrid(CropList);
